import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { Star, CheckCircle, XCircle, Clock, Calendar, Filter, Sparkles, Paperclip, ChevronRight } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';

const COMPLETION_LABEL = {
  completed_successfully: { label: 'Completed', color: 'text-emerald-700 bg-emerald-100', Icon: CheckCircle },
  partially_completed: { label: 'Partial', color: 'text-amber-700 bg-amber-100', Icon: Clock },
  student_absent: { label: 'Student Absent', color: 'text-slate-500 bg-slate-100', Icon: XCircle },
  teacher_absent: { label: 'Teacher Absent', color: 'text-slate-500 bg-slate-100', Icon: XCircle },
  rescheduled: { label: 'Rescheduled', color: 'text-blue-700 bg-blue-100', Icon: Calendar },
  technical_issue: { label: 'Tech Issue', color: 'text-red-700 bg-red-100', Icon: XCircle },
};

const MOOD_EMOJI = { excellent: '😀', happy: '🙂', neutral: '😐', confused: '😕', low: '😞' };

function Stars({ n = 0 }) {
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map(k => (
        <Star key={k} className={`w-3.5 h-3.5 ${k <= n ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`} />
      ))}
    </span>
  );
}

/**
 * Chronological class-feedback timeline used by student, parent, teacher, admin.
 * Filters: date range, course, teacher, completion status.
 */
export default function FeedbackTimeline({ studentId, dataTestidPrefix = 'timeline' }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [courseFilter, setCourseFilter] = useState('all');
  const [teacherFilter, setTeacherFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const params = {};
      if (courseFilter !== 'all') params.course = courseFilter;
      if (teacherFilter !== 'all') params.teacher_id = teacherFilter;
      if (statusFilter !== 'all') params.completion_status = statusFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await api.get(`/feedback/student/${studentId}/timeline`, { params });
      setItems(res.data.items || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, [studentId, courseFilter, teacherFilter, statusFilter, dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const courses = Array.from(new Set(items.map(i => i.course || i.vertical).filter(Boolean)));
  const teachers = Array.from(new Map(items.map(i => [i.teacher_id, i.teacher_name])).entries()).filter(([id]) => id);

  return (
    <div className="space-y-4" data-testid={`${dataTestidPrefix}-root`}>
      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-3 flex flex-wrap items-end gap-3">
        <div className="flex items-center gap-1 text-xs font-bold text-slate-600"><Filter className="w-3.5 h-3.5" /> Filters</div>
        <div className="w-40">
          <label className="text-[10px] font-bold text-slate-500 uppercase">Course</label>
          <Select value={courseFilter} onValueChange={setCourseFilter}>
            <SelectTrigger className="h-8 text-xs" data-testid={`${dataTestidPrefix}-course`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All courses</SelectItem>
              {courses.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-44">
          <label className="text-[10px] font-bold text-slate-500 uppercase">Teacher</label>
          <Select value={teacherFilter} onValueChange={setTeacherFilter}>
            <SelectTrigger className="h-8 text-xs" data-testid={`${dataTestidPrefix}-teacher`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All teachers</SelectItem>
              {teachers.map(([id, name]) => <SelectItem key={id} value={id}>{name || id}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-40">
          <label className="text-[10px] font-bold text-slate-500 uppercase">Status</label>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-8 text-xs" data-testid={`${dataTestidPrefix}-status`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {Object.entries(COMPLETION_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] font-bold text-slate-500 uppercase">From</label>
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-8 text-xs w-36" data-testid={`${dataTestidPrefix}-date-from`} />
        </div>
        <div>
          <label className="text-[10px] font-bold text-slate-500 uppercase">To</label>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-8 text-xs w-36" data-testid={`${dataTestidPrefix}-date-to`} />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="text-center py-10 text-sm text-slate-500">Loading feedback timeline…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-10 text-sm text-slate-500 bg-white rounded-xl border border-slate-200">
          <Sparkles className="w-6 h-6 mx-auto mb-1 text-slate-300" />
          No class feedback yet
        </div>
      ) : (
        <ol className="relative border-l-2 border-indigo-100 ml-3 space-y-4">
          {items.map(it => {
            const meta = COMPLETION_LABEL[it.completion_status] || { label: it.completion_status || '—', color: 'text-slate-500 bg-slate-100', Icon: Clock };
            const Icon = meta.Icon;
            const isOpen = !!expanded[it.id];
            return (
              <li key={it.id} className="pl-6 relative" data-testid={`${dataTestidPrefix}-item-${it.id}`}>
                <span className={`absolute -left-[11px] top-0 w-5 h-5 rounded-full ${meta.color} flex items-center justify-center border-2 border-white`}>
                  <Icon className="w-3 h-3" />
                </span>
                <div className="bg-white rounded-xl border border-slate-200 p-4 hover:shadow-sm transition">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className="text-xs font-bold text-slate-900">{it.class_date} · {it.class_time || '—'}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Class #{it.class_number || '—'}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${meta.color}`}>{meta.label}</span>
                    {it.student_mood && <span className="text-base" title={it.student_mood}>{MOOD_EMOJI[it.student_mood] || ''}</span>}
                  </div>
                  <p className="text-sm font-bold text-slate-900">{it.topic_name || it.course || 'Class'}</p>
                  <p className="text-xs text-slate-500">by {it.teacher_name || '—'}</p>
                  {it.parent_update && <p className="text-sm text-slate-700 mt-2 italic">&ldquo;{it.parent_update}&rdquo;</p>}

                  <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-slate-600">
                    {it.understanding_stars > 0 && (
                      <span className="inline-flex items-center gap-1"><span className="font-bold">Understanding</span> <Stars n={it.understanding_stars} /></span>
                    )}
                    {it.participation && <span><span className="font-bold">Participation:</span> {it.participation}</span>}
                    {it.behaviour && <span><span className="font-bold">Behaviour:</span> {it.behaviour}</span>}
                    {(it.homework_attachments || []).length > 0 && (
                      <span className="inline-flex items-center gap-1 text-indigo-700"><Paperclip className="w-3 h-3" /> {(it.homework_attachments || []).length} attachment{(it.homework_attachments || []).length > 1 ? 's' : ''}</span>
                    )}
                  </div>

                  <button
                    onClick={() => setExpanded(s => ({ ...s, [it.id]: !s[it.id] }))}
                    className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-indigo-600 hover:text-indigo-800"
                    data-testid={`${dataTestidPrefix}-expand-${it.id}`}
                  >
                    <ChevronRight className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                    {isOpen ? 'Hide details' : 'Show details'}
                  </button>

                  {isOpen && (
                    <div className="mt-3 space-y-2 text-xs text-slate-700 border-t border-slate-100 pt-3">
                      {it.topics_covered && <div><span className="font-bold">Topics:</span><pre className="whitespace-pre-wrap font-sans">{it.topics_covered}</pre></div>}
                      {(it.objectives || []).length > 0 && (
                        <div>
                          <span className="font-bold">Objectives:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(it.objectives || []).map(o => <span key={o} className="px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 text-[10px] font-bold">{o}</span>)}
                          </div>
                        </div>
                      )}
                      {it.homework_text && <div><span className="font-bold">Homework:</span><pre className="whitespace-pre-wrap font-sans">{it.homework_text}</pre></div>}
                      {(it.homework_attachments || []).length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-1">
                          {(it.homework_attachments || []).map(a => (
                            <a key={a.file_id} href={a.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-slate-100 border border-slate-200 text-xs font-semibold text-slate-700 hover:text-indigo-700">
                              <Paperclip className="w-3 h-3" />{a.filename || 'File'}
                            </a>
                          ))}
                        </div>
                      )}
                      {it.next_class_plan && <div><span className="font-bold">Next class:</span> {it.next_class_plan}</div>}
                    </div>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
